import { Company } from '@shared-ikp/model/company.model';
import { findCompanyByGpNr } from '../../../service/company/company.utils';
import { Kunde } from '../../../../generated/model/kunde.model';
import {
  AccountVertragspartnerFormValue,
  NeuerVertragspartnerFormValue,
  VerwalteterVertragspartnerFormValue,
} from '../schemas/neuer-vertragspartner.schema';
import { PrivatUnternehmenFormValue, RechnungsempfaengerFormValue, VertragspartnerMode } from '../schemas';
import { isUnternehmen } from './beteiligte-partei.util';
import { Unternehmen } from '../../../../generated/model/unternehmen.model';
import { Adresse } from '../../../../generated/model/adresse.model';
import {
  initialAccountVertragspartnerFormValue,
  initialNeuerRechnungsempfaengerFormValue,
  initialVerwalteterVertragspartnerFormValue,
  mapAbweichenderRechnungsempfaengerToUnternehmen,
  mapAdresseFormValueToAdresse,
  mapAdresseToFormValue,
  mapKundeToPrivatUnternehmenFormValue,
  mapPrivatUnternehmenFormValueToKunde,
  mapRechnungsempfaengerFormValueToUnternehmen,
} from './common-vertragspartner.mapper';
import { mapAddressToAdresse, mapGeschaeftspartnerToUnternehmen } from './bisheriger-vertragspartner.mapper';

/**
 * Everything step 4 preselects for the user, resolved from the account's companies the store
 * loaded. Applied while the draft is built (and again when a radio switch clears a branch), so the
 * defaults are part of the form value from the start instead of being written into it afterwards.
 */
export interface NeuerVertragspartnerDefaults {
  accountUnternehmen: Company | null;
  rechnungsempfaengerUnternehmen: Company | null;
  verwalteteUnternehmen: Company[];
}

const NO_DEFAULTS: NeuerVertragspartnerDefaults = {
  accountUnternehmen: null,
  rechnungsempfaengerUnternehmen: null,
  verwalteteUnternehmen: [],
};

export function defaultAccountVertragspartnerFormValue(accountUnternehmen: Company | null): AccountVertragspartnerFormValue {
  if (!accountUnternehmen) {
    return initialAccountVertragspartnerFormValue();
  }

  const unternehmen = mapGeschaeftspartnerToUnternehmen(accountUnternehmen);
  return {
    ...initialAccountVertragspartnerFormValue(),
    name: unternehmen.name ?? '',
    kundennummer: unternehmen.kundennummer ?? '',
    adresse: mapAdresseToFormValue(unternehmen.adresse),
    uid: unternehmen.uid ?? '',
    firmenbuchnummer: unternehmen.firmenbuchnummer ?? '',
  };
}

export function defaultVerwalteterVertragspartnerFormValue(
  rechnungsempfaengerUnternehmen: Company | null,
): VerwalteterVertragspartnerFormValue {
  return {
    ...initialVerwalteterVertragspartnerFormValue(),
    rechnungsempfaenger: defaultRechnungsempfaengerFormValue(rechnungsempfaengerUnternehmen),
  };
}

export function defaultMieterEigentuemerFormValue(): PrivatUnternehmenFormValue {
  return mapKundeToPrivatUnternehmenFormValue(null);
}

function defaultRechnungsempfaengerFormValue(rechnungsempfaengerUnternehmen: Company | null): RechnungsempfaengerFormValue {
  if (!rechnungsempfaengerUnternehmen) {
    return initialNeuerRechnungsempfaengerFormValue();
  }

  return mapUnternehmenToRechnungsempfaengerFormValue(mapGeschaeftspartnerToUnternehmen(rechnungsempfaengerUnternehmen), false);
}

function withRefreshedVerwaltetesUnternehmen(
  verwalteterVertragspartner: VerwalteterVertragspartnerFormValue,
  verwalteteUnternehmen: Company[],
): VerwalteterVertragspartnerFormValue {
  const company = findCompanyByGpNr(verwalteteUnternehmen, verwalteterVertragspartner.kundennummer);

  if (!company) {
    return verwalteterVertragspartner;
  }

  return {
    ...verwalteterVertragspartner,
    adresse: mapAdresseToFormValue(mapAddressToAdresse(company.address)),
    uid: company.uid ?? '',
    email: company.email ?? '',
  };
}

export function mapNeuerVertragsPartnerToFormValue(
  neuerKunde: Kunde | null | undefined,
  defaults: NeuerVertragspartnerDefaults = NO_DEFAULTS,
): NeuerVertragspartnerFormValue {
  const neuerVertragspartnerArt = mapKundeTypToNeuerVertragspartnerArt(neuerKunde?.kundeTyp);
  return {
    neuerVertragspartnerArt,
    verwalteterVertragspartner: toVerwalteterVertragspartner(neuerKunde, neuerVertragspartnerArt, defaults),
    accountVertragspartner: toAccountVertragspartner(neuerKunde, neuerVertragspartnerArt, defaults),
    mieterEigentuemer: toMieterEigentuemer(neuerKunde, neuerVertragspartnerArt),
  };
}

/**
 * Each branch is either what was saved for it, or the preselection it starts out with — never a
 * merge of both. A branch the Kunde was not saved under holds its preselection, so switching to it
 * shows the same defaults a fresh Ummeldung would.
 */
function toVerwalteterVertragspartner(
  neuerKunde: Kunde | null | undefined,
  neuerVertragspartnerArt: VertragspartnerMode | null,
  defaults: NeuerVertragspartnerDefaults,
): VerwalteterVertragspartnerFormValue {
  if (neuerVertragspartnerArt !== VertragspartnerMode.VERWALTETER_KUNDE) {
    return defaultVerwalteterVertragspartnerFormValue(defaults.rechnungsempfaengerUnternehmen);
  }

  const gespeicherterRechnungsempfaenger = storedRechnungsempfaenger(neuerKunde);
  const rechnungsempfaenger = gespeicherterRechnungsempfaenger
    ? toRechnungsempfaengerFormValue(gespeicherterRechnungsempfaenger)
    : defaultRechnungsempfaengerFormValue(defaults.rechnungsempfaengerUnternehmen);

  const unternehmen = isUnternehmen(neuerKunde?.vertragspartner) ? neuerKunde.vertragspartner : null;
  if (!unternehmen) {
    return { ...initialVerwalteterVertragspartnerFormValue(), rechnungsempfaenger };
  }

  return withRefreshedVerwaltetesUnternehmen(
    {
      name: unternehmen.name ?? null,
      kundennummer: unternehmen.kundennummer ?? null,
      firmenbuchnummer: unternehmen.firmenbuchnummer ?? null,
      uid: unternehmen.uid ?? null,
      adresse: mapAdresseToFormValue(unternehmen.adresse),
      telefon: neuerKunde?.telefon ?? null,
      email: neuerKunde?.email ?? null,
      rechnungsempfaenger,
    },
    defaults.verwalteteUnternehmen,
  );
}

function toAccountVertragspartner(
  neuerKunde: Kunde | null | undefined,
  neuerVertragspartnerArt: VertragspartnerMode | null,
  defaults: NeuerVertragspartnerDefaults,
): AccountVertragspartnerFormValue {
  if (neuerVertragspartnerArt !== VertragspartnerMode.ACCOUNT_KUNDE) {
    return defaultAccountVertragspartnerFormValue(defaults.accountUnternehmen);
  }

  const rechnungsempfaenger = toRechnungsempfaengerFormValue(storedRechnungsempfaenger(neuerKunde));
  const unternehmen = isUnternehmen(neuerKunde?.vertragspartner) ? neuerKunde.vertragspartner : null;
  if (!unternehmen) {
    return { ...defaultAccountVertragspartnerFormValue(defaults.accountUnternehmen), rechnungsempfaenger };
  }

  return {
    name: unternehmen.name ?? '',
    kundennummer: unternehmen.kundennummer ?? '',
    adresse: mapAdresseToFormValue(unternehmen.adresse),
    uid: unternehmen.uid ?? '',
    firmenbuchnummer: unternehmen.firmenbuchnummer ?? '',
    rechnungsempfaenger,
  };
}

function toMieterEigentuemer(
  neuerKunde: Kunde | null | undefined,
  neuerVertragspartnerArt: VertragspartnerMode | null,
): PrivatUnternehmenFormValue {
  if (neuerVertragspartnerArt !== VertragspartnerMode.BEARBEITBAR) {
    return defaultMieterEigentuemerFormValue();
  }

  return {
    ...mapKundeToPrivatUnternehmenFormValue(neuerKunde),
    rechnungsempfaenger: toRechnungsempfaengerFormValue(storedRechnungsempfaenger(neuerKunde)),
  };
}

function storedRechnungsempfaenger(neuerKunde: Kunde | null | undefined): Unternehmen | null {
  return isUnternehmen(neuerKunde?.rechnungsempfaenger) ? neuerKunde.rechnungsempfaenger : null;
}

function toRechnungsempfaengerFormValue(unternehmen: Unternehmen | null): RechnungsempfaengerFormValue {
  return mapUnternehmenToRechnungsempfaengerFormValue(unternehmen, !!unternehmen);
}

export function companyAsVerwalteterVertragspartner(
  company: Company | null,
  rechnungsempfaenger: RechnungsempfaengerFormValue,
): VerwalteterVertragspartnerFormValue {
  return {
    name: company?.name ?? '',
    kundennummer: company?.gpNr ?? '',
    adresse: mapAdresseToFormValue(company ? mapAddressToAdresse(company.address) : null),
    uid: company?.uid ?? '',
    firmenbuchnummer: '',
    telefon: '',
    email: company?.email ?? '',
    rechnungsempfaenger,
  };
}

export function companyAsAccountVertragspartner(
  company: Company | null,
  rechnungsempfaenger: RechnungsempfaengerFormValue,
): AccountVertragspartnerFormValue {
  const unternehmen = company ? mapGeschaeftspartnerToUnternehmen(company) : null;

  return {
    name: unternehmen?.name ?? '',
    kundennummer: unternehmen?.kundennummer ?? '',
    adresse: mapAdresseToFormValue(unternehmen?.adresse),
    uid: unternehmen?.uid ?? '',
    firmenbuchnummer: unternehmen?.firmenbuchnummer ?? '',
    rechnungsempfaenger,
  };
}

export function companyAsRechnungsempfaenger(
  company: Company | null,
  hasAbweichendenRechnungsempfaenger: boolean,
): RechnungsempfaengerFormValue {
  const unternehmen = company ? mapGeschaeftspartnerToUnternehmen(company) : null;

  return {
    hasAbweichendenRechnungsempfaenger,
    name: unternehmen?.name ?? '',
    kundennummer: unternehmen?.kundennummer ?? '',
    adresse: mapAdresseToFormValue(unternehmen?.adresse),
    uid: unternehmen?.uid ?? '',
    firmenbuchnummer: unternehmen?.firmenbuchnummer ?? '',
  };
}

export function buildNeuerKunde(draft: NeuerVertragspartnerFormValue, zaehlpunktAdresse?: Adresse | null): Kunde | undefined {
  if (draft.neuerVertragspartnerArt === null) {
    return undefined;
  }
  if (draft.neuerVertragspartnerArt === VertragspartnerMode.VERWALTETER_KUNDE) {
    return mapVerwalteterVertragspartnerToKundVertragspartner(draft);
  }
  if (draft.neuerVertragspartnerArt === VertragspartnerMode.BEARBEITBAR) {
    return mapPrivatUnternehmenFormValueToKunde(
      draft?.mieterEigentuemer,
      zaehlpunktAdresse ? mapAdresseToFormValue(zaehlpunktAdresse) : null,
    );
  }

  return {
    kundeTyp: Kunde.KundeTypEnum.ACCOUNT_KUNDE,
    vertragspartner: mapAccountVertragspartnerFormValueToUnternehmen(draft.accountVertragspartner),
    rechnungsempfaenger: mapAbweichenderRechnungsempfaengerToUnternehmen(draft.accountVertragspartner.rechnungsempfaenger),
  };
}

function mapAccountVertragspartnerFormValueToUnternehmen(vertragspartner: AccountVertragspartnerFormValue): Unternehmen | null {
  if (!vertragspartner.kundennummer.trim()) {
    return null;
  }

  return {
    name: vertragspartner.name.trim(),
    kundennummer: vertragspartner.kundennummer.trim(),
    uid: vertragspartner.uid.trim(),
    firmenbuchnummer: vertragspartner.firmenbuchnummer.trim(),
    adresse: mapAdresseFormValueToAdresse(vertragspartner.adresse),
  };
}

function mapKundeTypToNeuerVertragspartnerArt(kundeTyp?: Kunde.KundeTypEnum) {
  if (kundeTyp === undefined || kundeTyp === null) {
    return null;
  }

  switch (kundeTyp) {
    case Kunde.KundeTypEnum.ACCOUNT_KUNDE:
      return VertragspartnerMode.ACCOUNT_KUNDE;
    case Kunde.KundeTypEnum.EXTERNER_KUNDE:
      return VertragspartnerMode.BEARBEITBAR;
    case Kunde.KundeTypEnum.VERWALTETER_KUNDE:
      return VertragspartnerMode.VERWALTETER_KUNDE;
  }
}

function mapVerwalteterVertragspartnerToKundVertragspartner(draft: NeuerVertragspartnerFormValue): Kunde {
  const rechnungsempfaenger = draft.verwalteterVertragspartner.rechnungsempfaenger;

  return {
    kundeTyp: Kunde.KundeTypEnum.VERWALTETER_KUNDE,
    vertragspartner: {
      name: draft?.verwalteterVertragspartner?.name ?? null,
      uid: draft?.verwalteterVertragspartner?.uid ?? null,
      kundennummer: draft?.verwalteterVertragspartner?.kundennummer ?? null,
      firmenbuchnummer: draft?.verwalteterVertragspartner?.firmenbuchnummer ?? null,
      adresse: mapAdresseFormValueToAdresse(draft?.verwalteterVertragspartner?.adresse),
    },
    email: draft?.verwalteterVertragspartner?.email,
    telefon: draft?.verwalteterVertragspartner?.telefon,
    rechnungsempfaenger: rechnungsempfaenger.kundennummer ? mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaenger) : null,
  };
}

function mapUnternehmenToRechnungsempfaengerFormValue(
  unternehmen: Unternehmen | null | undefined,
  hasAbweichendenRechnungsempfaenger = !!unternehmen,
): RechnungsempfaengerFormValue {
  const { name, kundennummer, adresse, uid, firmenbuchnummer } = unternehmen ?? {};
  return {
    hasAbweichendenRechnungsempfaenger,
    name: name ?? '',
    kundennummer: kundennummer ?? '',
    adresse: mapAdresseToFormValue(adresse),
    uid: uid ?? '',
    firmenbuchnummer: firmenbuchnummer ?? '',
  };
}
